import React, { useState, useEffect } from 'react';
import useMeasure from 'react-use-measure';
import { motion, AnimatePresence } from 'framer-motion';
import { Flex, Image, IconButton, Text, Center } from '@chakra-ui/react';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';
import { PortableText } from '@portabletext/react';

import Link from '@rsc/ui-components/tailwind/Link';

const variants = {
  enter: ({ direction, width }) => ({ x: direction * width }),
  center: { x: 0 },
  exit: ({ direction, width }) => ({ x: direction * -width }),
};

const components = {
  marks: {
    link: ({ children, value }) => (
      <Link className="text-brand-foreground underline" link={value}>
        {children}
      </Link>
    ),
    strong: ({ children }) => <b color="primary.base">{children}</b>,
    em: ({ children }) => <em color="primary.base">{children}</em>,
  },
  block: {
    normal: ({ children }) => (
      <Text
        noOfLines={2}
        color="primary.base"
        textAlign="center"
        textStyle="caption"
        fontWeight="bold"
        as="p"
        my={4}
      >
        {children}
      </Text>
    ),
  },
};

function usePrevious(state) {
  const [tuple, setTuple] = useState([null, state]);
  if (tuple[1] !== state) setTuple([tuple[1], state]);
  return tuple[0];
}

interface NotificationCarouselProps {
  notificationBanner: {
    blockSvg?: {
      svgUploaded?: {
        asset?: {
          url?: string;
        };
        alt?: string;
      };
    };
    block?: object[];
  }[];
  atTopOfPage: boolean;
  menuOpen: boolean;
}

export default function NotificationCarousel({
  notificationBanner,
  atTopOfPage,
  menuOpen,
}: Readonly<NotificationCarouselProps>) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [ref, { width }] = useMeasure();
  const prev = usePrevious(currentIndex);
  const direction = currentIndex > prev ? 1 : -1;

  const handleNext = () => {
    setCurrentIndex(prevIndex =>
      prevIndex + 1 >= notificationBanner.length ? 0 : prevIndex + 1
    );
  };
  const handlePrevious = () => {
    setCurrentIndex(prevIndex =>
      prevIndex - 1 < 0 ? notificationBanner.length - 1 : prevIndex - 1
    );
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex(prevIndex =>
        prevIndex + 1 >= notificationBanner.length ? 0 : prevIndex + 1
      );
    }, 5000);
    return () => clearInterval(interval);
  }, [currentIndex, notificationBanner?.length]);

  return (
    <Center
      bg="white"
      w="100vw"
      h={atTopOfPage && !menuOpen ? `10` : '0px'}
      transition="height .3s ease-in-out"
      visibility={atTopOfPage && !menuOpen ? 'visible' : 'hidden'}
      maxH="10"
    >
      <Flex
        role="banner"
        aria-labelledby="carousel"
        align="center"
        content="center"
        maxWidth="2xl"
        w="full"
        direction="row"
        overflow="hidden"
        ref={ref}
        h="10"
        maxH="10"
        textOverflow="ellipsis"
        webkit-line-clamp="2"
      >
        <IconButton
          aria-label="Previous"
          icon={<MdKeyboardArrowLeft size={16} />}
          onClick={() => handlePrevious()}
          variant="link"
        />
        <Flex flex="1" align="center" justify="center">
          <AnimatePresence
            mode="wait"
            initial={false}
            custom={{ direction, width }}
          >
            <motion.div
              key={currentIndex}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              custom={{ direction, width }}
              transition={{
                x: { type: 'spring', bounce: 0 },
                opacity: { duration: 0.2 },
              }}
            >
              <Flex gap="2" direction="row" align="center" justify="center">
                {notificationBanner[currentIndex]?.blockSvg && (
                  <Image
                    alt={
                      notificationBanner[currentIndex].blockSvg?.svgUploaded
                        ?.alt || 'Image'
                    }
                    src={
                      notificationBanner[currentIndex]?.blockSvg?.svgUploaded
                        ?.asset?.url
                    }
                    width="24px"
                  />
                )}
                <PortableText
                  // @ts-expect-error value is needed
                  value={notificationBanner[currentIndex].block}
                  // @ts-expect-error components value is needed
                  components={components}
                  listNestingMode={undefined}
                />
              </Flex>
            </motion.div>
          </AnimatePresence>
        </Flex>
        <IconButton
          aria-label="Next"
          icon={<MdKeyboardArrowRight size={16} />}
          onClick={() => handleNext()}
          variant="link"
        />
      </Flex>
    </Center>
  );
}
